import { useEffect, useState } from "react";
import {
  X,
  FloppyDisk,
  UploadSimple,
  Camera,
  ImageSquare,
} from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { disciplines as mockDisciplines } from "@/data/mockData";

function mediaUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function EditProfileDrawer({ user, onClose, onSaved }) {
  const { disciplines: liveDisciplines } = useBrand();
  const disciplines = liveDisciplines && liveDisciplines.length ? liveDisciplines : mockDisciplines;

  const [form, setForm] = useState({
    name: user.name || "",
    city: user.city || "",
    country: user.country || "",
    short_bio: user.short_bio || "",
    long_bio: user.long_bio || "",
    photo: user.photo || "",
    cover: user.cover || "",
    primary_discipline: user.primary_discipline || "",
    sub_disciplines: Array.isArray(user.sub_disciplines) ? user.sub_disciplines : [],
    links: {
      website: user.links?.website || "",
      instagram: user.links?.instagram || "",
      spotify: user.links?.spotify || "",
      soundcloud: user.links?.soundcloud || "",
      youtube: user.links?.youtube || "",
    },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);

  const uploadAs = async (field, file) => {
    if (!file) return;
    setError("");
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [field]: data.url }));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch("/users/me", form);
      onSaved?.(data.user);
      onClose();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-profile-drawer"
      className="fixed inset-0 z-[80] bg-black/60 flex items-start justify-end"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="h-full w-full max-w-xl bg-[#050505] border-l border-[#27272A] overflow-auto">
        <header className="sticky top-0 bg-[#050505] border-b border-[#27272A] p-5 flex items-center justify-between z-10">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
              // EDITAR MI PERFIL
            </div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
              @{user.handle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 border border-[#27272A] inline-flex items-center justify-center hover:border-[#FF0033] hover:text-[#FF0033]"
          >
            <X size={14} />
          </button>
        </header>

        <div className="p-5 space-y-6">
          {/* Cover */}
          <div>
            <Label>// PORTADA</Label>
            <div className="mt-2 relative aspect-[16/6] bg-[#121212] border border-[#27272A] overflow-hidden group">
              {form.cover ? (
                <img
                  src={mediaUrl(form.cover)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-[#525252] tracking-[0.22em] uppercase">
                  · sin portada ·
                </div>
              )}
              <label className="absolute bottom-2 right-2 bg-black/70 border border-[#27272A] hover:border-[#FF3E00] font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 cursor-pointer flex items-center gap-2">
                <ImageSquare size={12} />
                {uploading === "cover" ? "Subiendo…" : "Cambiar portada"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadAs("cover", e.target.files?.[0])}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Photo */}
          <div>
            <Label>// FOTO DE PERFIL</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-20 w-20 bg-[#121212] border border-[#27272A] overflow-hidden">
                {form.photo ? (
                  <img
                    src={mediaUrl(form.photo)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer border border-[#27272A] hover:border-[#FF3E00] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em]">
                <Camera size={14} />
                {uploading === "photo" ? "Subiendo…" : "Cambiar foto"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadAs("photo", e.target.files?.[0])}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Text fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field
              label="// NOMBRE"
              value={form.name}
              onChange={(v) => setForm((f) => ({ ...f, name: v }))}
            />
            <Field
              label="// CIUDAD"
              value={form.city}
              onChange={(v) => setForm((f) => ({ ...f, city: v }))}
            />
          </div>
          <Field
            label="// BIO CORTA (1 línea)"
            value={form.short_bio}
            onChange={(v) => setForm((f) => ({ ...f, short_bio: v }))}
          />
          <div>
            <Label>// BIO LARGA</Label>
            <textarea
              data-testid="edit-long-bio"
              rows={4}
              value={form.long_bio}
              onChange={(e) =>
                setForm((f) => ({ ...f, long_bio: e.target.value }))
              }
              className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-3 font-display text-[14px] resize-none"
            />
          </div>

          {/* Disciplines */}
          <div>
            <Label>// DISCIPLINA PRIMARIA</Label>
            <div data-testid="edit-disciplines" className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
              {disciplines.map((d) => {
                const active = form.primary_discipline === d.slug;
                return (
                  <button
                    type="button"
                    key={d.slug}
                    data-testid={`edit-discipline-${d.slug}`}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        primary_discipline: d.slug,
                        sub_disciplines: [],
                      }))
                    }
                    className={`text-left p-3 border transition-all ${
                      active
                        ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                        : "border-[#27272A] bg-[#0a0a0a] text-[#F4F4F5] hover:border-[#F4F4F5]"
                    }`}
                  >
                    <div className="font-display font-black uppercase tracking-tight text-sm leading-tight">
                      {d.name}
                    </div>
                    <div
                      className={`font-mono text-[10px] tracking-wider mt-1 ${
                        active ? "text-white/70" : "text-[#A1A1AA]"
                      }`}
                    >
                      {d.tag}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {form.primary_discipline ? (
            <div>
              <Label>
                // SUB-ÁREAS (HASTA 3){" "}
                <span className="text-[#FF3E00]">
                  {form.sub_disciplines.length}/3
                </span>
              </Label>
              {(() => {
                const primary = disciplines.find(
                  (d) => d.slug === form.primary_discipline
                );
                const subs = primary?.sub_categories || [];
                if (subs.length === 0) {
                  return (
                    <div className="mt-2 border border-dashed border-[#27272A] p-4 font-mono text-[10px] text-[#525252] uppercase tracking-[0.22em] text-center">
                      · esta disciplina aún no tiene subcategorías ·
                    </div>
                  );
                }
                return (
                  <div data-testid="edit-subcat-list" className="mt-2 flex flex-wrap gap-2">
                    {subs.map((sc) => {
                      const label = sc.name;
                      const active = form.sub_disciplines.includes(label);
                      const disabled = !active && form.sub_disciplines.length >= 3;
                      return (
                        <button
                          type="button"
                          key={sc.slug}
                          data-testid={`edit-subcat-${sc.slug}`}
                          disabled={disabled}
                          onClick={() => {
                            if (disabled) return;
                            setForm((f) => ({
                              ...f,
                              sub_disciplines: active
                                ? f.sub_disciplines.filter((s) => s !== label)
                                : [...f.sub_disciplines, label],
                            }));
                          }}
                          className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                            active
                              ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                              : disabled
                              ? "border-[#27272A] text-[#525252] cursor-not-allowed"
                              : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5]"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ) : null}

          {/* Links */}
          <div>
            <Label>// ENLACES</Label>
            <div className="mt-2 space-y-2">
              {Object.entries(form.links).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[110px_1fr] gap-2 items-center">
                  <span className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] uppercase">
                    {k}
                  </span>
                  <input
                    data-testid={`link-${k}`}
                    value={v}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        links: { ...f.links, [k]: e.target.value },
                      }))
                    }
                    placeholder={`https://${k}.com/@tu_user`}
                    className="bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2 font-mono text-[12px]"
                  />
                </div>
              ))}
            </div>
          </div>

          {error ? (
            <div className="border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2 font-mono text-[11px] text-[#FF8899]">
              {error}
            </div>
          ) : null}

          <div className="pt-4 border-t border-[#27272A] flex items-center gap-3">
            <button
              data-testid="edit-profile-save"
              onClick={save}
              disabled={saving}
              className={`btn-brutal inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-bold px-5 py-3 ${
                saving
                  ? "bg-[#121212] text-[#525252]"
                  : "bg-[#FF3E00] text-white"
              }`}
            >
              <FloppyDisk size={14} weight="fill" />
              {saving ? "Guardando…" : "Guardar"}
            </button>
            <button
              onClick={onClose}
              className="font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 text-[#A1A1AA] hover:text-white"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
      {children}
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2.5 font-display text-[14px]"
      />
    </div>
  );
}
